import { IServiceCategoryDomainValue, ITranslateProps } from 'models/common';
import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { BookingInputField, BookingSelectMenu } from 'components/FormControls';
import {
	aboveZero,
	discountFieldNormalization,
	isNumber,
	maxValue,
	priceFieldNormalization,
	required,
} from 'helpers/validationattributes';
import { Field } from 'redux-form';
import { IShopCard } from 'models/shop/shopcard';
import { IBundleForm, IBundleFormValues, IServicesList, IShopBookingTemplates } from 'models/shop/shopBookingTemplates';
import renderIcon from 'helpers/renderIcon';
import { formatCurrency, formatDuration, getCurrencySymbol, readURLSearchParams } from 'helpers/common';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { SortableElement, SortableContainer, SortableHandle } from 'react-sortable-hoc';
import { moveElement } from '../../helpers';
import * as i18n from 'i18next';

const priceRequired = (val) => required(typeof val == 'number' && !isNaN(val) && isFinite(val) ? String(val) : val);
const maxValue100 = maxValue(100);

interface IProps extends ITranslateProps {
	officeId: string;
	shopCard: IShopCard;
	currentFormValues: IBundleForm;
	mode: 'add' | 'edit';
	serviceGroups: IServiceGroup[];
	servicesToEdit: IServicesList[];
	editModeServiceGroupId: string;
	bookingTemplates: IShopBookingTemplates;
	serviceCategories: IServiceCategoryDomainValue[];
	selectedServices: string[];
	openAddServicesToBundleModal: (
		serviceCategories,
		serviceGroups,
		bookingTemplates,
		mode,
		handleServicesSelected,
		selectedServices,
		currencyKey,
	) => void;
	handleServicesSelected: (serviceIds: string[]) => void;

	// form props
	change: any;
	initialize: (data: Partial<IBundleFormValues>) => void;
}

interface IState {
	priceFieldCursor: number;
	hasSetEditBundles: boolean;
	/**
	 * this is used to generate the validation function for the custom price field
	 */
	originalPrice?: number;
	originalPriceValidation?: any;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class BundleSettings extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.priceFieldRef = React.createRef();

		this.state = {
			priceFieldCursor: null,
			hasSetEditBundles: false,
		};

		this.renderPrice = this.renderPrice.bind(this);
		this.validateCustomPrice = this.validateCustomPrice.bind(this);
	}

	private priceFieldRef;

	private pricingTypeValues = [
		{ order: 0, key: 1, value: this.props.t('User_ShopCard_Services_CustomPricing') },
		{ order: 1, key: 2, value: this.props.t('User_ShopCard_Services_Percentage') },
	];

	public componentDidMount() {
		// Set the intiial service settings when in add mode
		if (this.props.mode === 'add' && this.props.currentFormValues === null) {
			// Set the service category and group if the user came from service group dropdown Add component
			const serviceCategory = Number(readURLSearchParams(c.SERVICE_CATEGORY));
			const serviceGroup = readURLSearchParams(c.SERVICE_GROUP);

			this.props.change('bundle.pricing.type', 1);

			if (serviceCategory && serviceGroup) {
				this.props.change('info.serviceCategory', serviceCategory);
				this.props.change('info.serviceGroup', serviceGroup);
			}
		}
	}

	public componentDidUpdate(prevProps: IProps) {
		// Set initial edit values
		if (
			this.props.mode === 'edit' &&
			!this.state.hasSetEditBundles &&
			this.props.serviceGroups?.length &&
			this.props.servicesToEdit?.length &&
			this.props.servicesToEdit[0]?.childrenBookingTemplates
		) {
			const serviceGroupData = this.props.serviceGroups.find((sg) => sg.id === this.props.editModeServiceGroupId);
			const price = this.props.servicesToEdit[0].originalPrice;
			const childrenBookingTemplateIds =
				this.props.servicesToEdit[0].childrenBookingTemplates.map((cbt) => cbt.id) || null;

			const initialValuesObject: IBundleFormValues = {
				info: {
					bundleName: serviceGroupData.value,
					serviceCategory: serviceGroupData.serviceCategoryId,
					serviceGroup: serviceGroupData.parentId,
					serviceDescription: this.props.servicesToEdit[0].description,
				},
				bundle: {
					pricing: {
						type: 1,
						customPrice: price,
					},
					bundleSettings: {
						bookability: true,
						onlinePayment: true, // change once implemented
						onsitePayment: true, // change once implemented
					},
					info: {
						bookingTemplateId: this.props.servicesToEdit[0].id,
					},
				},
				initialChildrenBookingTemplateIds: childrenBookingTemplateIds, // For comparing if the order changed
			};

			const childrenServiceIds = this.props.servicesToEdit[0].childrenBookingTemplates.map((cbt) => cbt.id);
			this.setState({ hasSetEditBundles: true });
			this.props.handleServicesSelected(childrenServiceIds);
			this.props.initialize(initialValuesObject);
		}

		const newOriginalPrice = this.props.servicesToEdit?.[0]?.originalPrice;

		if (newOriginalPrice && this.state.originalPrice != newOriginalPrice) {
			this.setState({
				originalPrice: newOriginalPrice,
				originalPriceValidation: maxValue(newOriginalPrice),
			});
		}

		// Change flag to revalidate price
		if (prevProps?.selectedServices?.length !== this.props?.selectedServices?.length) {
			this.props.change('_validationHack', Date.now());
		}
	}

	public componentWillUnmount() {}

	private DragHandle = SortableHandle(() => (
		<div className="shopBundlesAddEdit__servicesContainer__dragIconContainer col-lg-1 col-2">
			{renderIcon('hamburger')}
		</div>
	));

	private SortableItem = SortableElement(({ element }) => {
		return (
			<div className="row align-items-center shopBundlesAddEdit__servicesContainer__sortableElement">
				<this.DragHandle />
				{element}
			</div>
		);
	});

	private SortableContainer = SortableContainer(({ children }) => {
		return <div className="container shopBundlesAddEdit__servicesContainer__sortableContainer">{children}</div>;
	});

	private onSortEnd = ({ oldIndex, newIndex }) => {
		const newArrayOfSelectedServices = moveElement([...this.props.selectedServices], oldIndex, newIndex);
		this.props.handleServicesSelected(newArrayOfSelectedServices);
	};

	private handleServiceDelete = (serviceId: string) => {
		const tempSelectedServices = [...this.props.selectedServices];
		const index = tempSelectedServices.indexOf(serviceId);
		tempSelectedServices.splice(index, 1);
		this.props.handleServicesSelected(tempSelectedServices);
	};

	private renderServices = () => {
		const { t } = this.props;

		const currencyKey = this.props?.shopCard?.currencyKey;
		const serviceElements: JSX.Element[] = [];

		this.props?.selectedServices.map((serviceId, idx) => {
			const service = this.props.bookingTemplates.services?.find((bt) => bt.id === serviceId);

			if (!service) {
				return;
			}

			let isLastElement = false;

			if (idx + 1 === this.props.selectedServices.length) {
				isLastElement = true;
			}

			const element = (
				<>
					<div className="col-lg-10 col-10">
						<div className="shopBundlesAddEdit__servicesContainer__nameContainer">
							<span className="shopBundlesAddEdit__servicesContainer__nameContainer__name">{service.name}</span>
						</div>
						<div className="shopBundlesAddEdit__servicesContainer__priceDurationContainer">
							<span className="shopBundlesAddEdit__servicesContainer__priceDurationContainer__duration">
								{formatDuration(service.duration)}
							</span>
							<span className="shopBundlesAddEdit__servicesContainer__priceDurationContainer__price">
								{formatCurrency(currencyKey, service.originalPrice)}
							</span>
						</div>
					</div>

					<div className="col-lg-1 shopBundlesAddEdit__bundleSettings__serviceDeleteDesktop">
						<div className="shopBundlesAddEdit__servicesContainer__deleteButtonContainer">
							{renderIcon('delete', undefined, undefined, undefined, () => this.handleServiceDelete(serviceId))}
						</div>
					</div>

					<div className="col-10 offset-2 shopBundlesAddEdit__bundleSettings__serviceDeleteMobile">
						<div
							className="shopBundlesAddEdit__bundleSettings__serviceDeleteButton"
							onClick={() => this.handleServiceDelete(serviceId)}
						>
							{renderIcon('delete', 16, 16, 'shopBundlesAddEdit__bundleSettings__serviceDeleteButton__icon')}
							<span>{t('BSPortal_Common_Remove')}</span>
						</div>
					</div>

					{isLastElement === false && (
						<div className="col-12">
							<div className="shopBundlesAddEdit__servicesContainer__sortableElement__mobileLine"></div>
						</div>
					)}
				</>
			);

			serviceElements.push(<this.SortableItem element={element} index={idx} key={serviceId} />);
		});

		return (
			<div className="shopBundlesAddEdit__servicesContainer">
				<this.SortableContainer onSortEnd={this.onSortEnd} useDragHandle={true}>
					{serviceElements}
				</this.SortableContainer>
				<div
					className="shopBundlesAddEdit__servicesContainer__addButton"
					onClick={() =>
						this.props.openAddServicesToBundleModal(
							this.props.serviceCategories,
							this.props.serviceGroups,
							this.props.bookingTemplates,
							this.props.mode,
							(serviceId) => this.props.handleServicesSelected(serviceId),
							this.props?.selectedServices,
							currencyKey,
						)
					}
				>
					{renderIcon('plus_thin', 12, 12, 'shopBundlesAddEdit__servicesContainer__addButton__plus')}
					<span>{t('User_ShopCard_Services_AddServices')}</span>
				</div>
			</div>
		);
	};

	private validateCustomPrice = (value) => {
		let max = this.props?.selectedServices.reduce((prev_price, serviceId) => {
			const service = this.props.bookingTemplates.services?.find((bt) => bt.id === serviceId);
			if (service) {
				return prev_price + service.originalPrice;
			}
			return prev_price;
		}, 0);
		max = Math.round(max * 100) / 100;
		return max && value && value > max ? i18n.t('Validation_Client_Field_MaxValue', { max }) : undefined;
	};

	private renderPrice = () => {
		const { t, currentFormValues, bookingTemplates } = this.props;
		const currencyKey = this.props?.shopCard?.currencyKey;

		if (!bookingTemplates.services.length) {
			return;
		}

		let originalPrice: number = 0;
		let discountPrice: number = 0;
		let totalPrice: number = 0;

		this.props?.selectedServices.map((serviceId) => {
			const service = this.props.bookingTemplates.services?.find((bt) => bt.id === serviceId);

			if (service) {
				originalPrice = originalPrice + service.originalPrice;
			}
		});

		totalPrice = originalPrice;

		// If custom pricing
		if (
			currentFormValues?.values?.bundle?.pricing.type === 1 &&
			currentFormValues?.values?.bundle?.pricing?.customPrice
		) {
			const customPrice = currentFormValues.values.bundle.pricing.customPrice;
			discountPrice = originalPrice - customPrice;
			totalPrice = customPrice;
		}

		// If percentage discount
		if (currentFormValues?.values?.bundle?.pricing.type === 2 && currentFormValues?.values?.bundle?.pricing?.discount) {
			const discount = currentFormValues.values.bundle.pricing.discount / 100; // percentage
			discountPrice = originalPrice * discount;
			totalPrice = originalPrice - originalPrice * discount;
		}

		return (
			<div className="shopBundlesAddEdit__priceContainer">
				<div className="row">
					<div className="col-md-6 col-12">
						<Field
							name={`bundle.pricing.type`}
							component={BookingSelectMenu}
							items={this.pricingTypeValues}
							type="text"
							label={t('User_ShopCard_Services_PricingType')}
							validate={[required]}
							placeholder={t('User_ShopCard_Services_CustomPricing')}
							information={t('User_ShopCard_Services_PricingType')}
							isRequired={true}
							searchable={false}
						/>
					</div>
					<div className="col-md-6 col-12">
						{currentFormValues?.values?.bundle?.pricing?.type === 1 ? (
							<Field
								name={`bundle.pricing.customPrice`}
								component={BookingInputField}
								type="text"
								label={`${t('Magazine_Section_Common_Price')} (${getCurrencySymbol(
									this.props?.shopCard?.currencyKey,
								)})`}
								validate={[priceRequired, isNumber, this.validateCustomPrice]}
								customRef={this.priceFieldRef}
								customCursor={this.state.priceFieldCursor}
								onChangeAction={(value) => {
									this.setState({ priceFieldCursor: value.target.selectionStart });
									const decimalValue = priceFieldNormalization(value.target.value);
									return (value.target.value = decimalValue);
								}}
								min={0}
								step={0.01}
								inputMode={'decimal'}
								normalize={aboveZero}
								information={`${t('Magazine_Section_Common_Price')} (${getCurrencySymbol(
									this.props?.shopCard?.currencyKey,
								)})`}
							/>
						) : (
							<Field
								name={`bundle.pricing.discount`}
								component={BookingInputField}
								type="text"
								label={t('User_ShopCard_Services_Discount')}
								validate={[priceRequired, isNumber, maxValue100]}
								onChangeAction={(value) => {
									this.setState({ priceFieldCursor: value.target.selectionStart });
									const decimalValue = discountFieldNormalization(value.target.value);
									return (value.target.value = decimalValue);
								}}
								min={0}
								step={0.1}
								inputMode={'decimal'}
								normalize={aboveZero}
								information={`${t('User_ShopCard_Services_Discount')} (%)`}
							/>
						)}
					</div>
				</div>

				<div className="shopBundlesAddEdit__priceContainer__border"></div>

				<div className="row">
					<div className="col-md-6 col-12">
						<div className="shopBundlesAddEdit__priceContainer__beforeDiscountText">
							<span className="shopBundlesAddEdit__priceContainer__beforeDiscountText__text">
								{t('User_ShopCard_Services_PriceBeforeDiscount')}
							</span>
						</div>
					</div>
					<div className="col-md-6 col-12">
						<div className="shopBundlesAddEdit__priceContainer__beforeDiscountPrice">
							<span className="shopBundlesAddEdit__priceContainer__beforeDiscountPrice__price">
								{formatCurrency(currencyKey, originalPrice)}
							</span>
						</div>
					</div>
					<div className="col-md-6 col-12">
						<div className="shopBundlesAddEdit__priceContainer__discountText">
							<span className="shopBundlesAddEdit__priceContainer__discountText__text">
								{t('User_ShopCard_Services_Discount')}
							</span>
						</div>
					</div>
					<div className="col-md-6 col-12">
						<div className="shopBundlesAddEdit__priceContainer__discountPrice">
							<span className="shopBundlesAddEdit__priceContainer__discountPrice__price">
								{formatCurrency(currencyKey, discountPrice)}
							</span>
						</div>
					</div>
					<div className="col-md-6 col-12">
						<div className="shopBundlesAddEdit__priceContainer__totalText">
							<span className="shopBundlesAddEdit__priceContainer__totalText__text">
								{t('User_ShopCard_Services_TotalBundlePrice')}
							</span>
						</div>
					</div>
					<div className="col-md-6 col-12">
						<div className="shopBundlesAddEdit__priceContainer__totalPrice">
							<span className="shopBundlesAddEdit__priceContainer__totalPrice__price">
								{formatCurrency(currencyKey, totalPrice)}
							</span>
						</div>
					</div>
				</div>
			</div>
		);
	};

	render() {
		const { t } = this.props;

		return (
			<div className="shopBundlesAddEdit__bundleSettings">
				<h2 className="shopBundlesAddEdit__bundleSettings__title">{t('User_ShopCard_Services_Services_Label')}</h2>
				<p className="shopBundlesAddEdit__bundleSettings__subText">{t('User_ShopCard_Services_AssignServices')}</p>
				{this.renderServices()}
				<h2 className="shopBundlesAddEdit__bundleSettings__title">{t('Shop_ManageShop_Products_Label_Price')}</h2>
				<p className="shopBundlesAddEdit__bundleSettings__subText">{t('User_ShopCard_Services_SetPriceBundle')}</p>
				{this.renderPrice()}
			</div>
		);
	}
}

export { BundleSettings };
